// src/features/task/form/ChecklistDraftEditor.tsx
// Checklists composed before the task exists, sent inline as
// `CreateTask.initial_checklists`.
//
// Typing is the whole interaction: Enter adds the next item and moves the
// caret to it, Backspace on an empty item removes it and moves back — the same
// contract as a notes app, so a checklist can be written without the mouse.

import { useCallback, useRef } from 'react';
import { useI18n } from '@react/hooks';
import { Icon, FORM_ICONS } from './icons';
import type { ChecklistDraft } from './draft';

export interface ChecklistDraftEditorProps {
  checklists: ChecklistDraft[];
  onChange: (checklists: ChecklistDraft[]) => void;
}

export function ChecklistDraftEditor({ checklists, onChange }: ChecklistDraftEditorProps) {
  const { t } = useI18n();
  // Item inputs keyed by draft item id, so focus never depends on a timeout.
  const itemRefs = useRef(new Map<string, HTMLInputElement>());
  const focusNext = useRef<string | null>(null);

  const registerItem = useCallback((id: string, el: HTMLInputElement | null) => {
    if (el) {
      itemRefs.current.set(id, el);
      if (focusNext.current === id) {
        focusNext.current = null;
        el.focus();
      }
    } else {
      itemRefs.current.delete(id);
    }
  }, []);

  const addChecklist = () => {
    onChange([...checklists, { title: t('task.checklist'), items: [] }]);
  };

  const removeChecklist = (index: number) => {
    onChange(checklists.filter((_, i) => i !== index));
  };

  const updateTitle = (index: number, title: string) => {
    onChange(checklists.map((cl, i) => (i === index ? { ...cl, title } : cl)));
  };

  const addItem = (listIndex: number, afterIndex?: number) => {
    const id = crypto.randomUUID();
    focusNext.current = id;
    onChange(
      checklists.map((cl, i) => {
        if (i !== listIndex) return cl;
        const items = [...cl.items];
        const at = afterIndex == null ? items.length : afterIndex + 1;
        items.splice(at, 0, { id, content: '', is_done: false });
        return { ...cl, items };
      }),
    );
  };

  const updateItem = (listIndex: number, itemIndex: number, content: string) => {
    onChange(
      checklists.map((cl, i) =>
        i !== listIndex
          ? cl
          : { ...cl, items: cl.items.map((it, j) => (j === itemIndex ? { ...it, content } : it)) },
      ),
    );
  };

  const toggleItem = (listIndex: number, itemIndex: number) => {
    onChange(
      checklists.map((cl, i) =>
        i !== listIndex
          ? cl
          : {
              ...cl,
              items: cl.items.map((it, j) => (j === itemIndex ? { ...it, is_done: !it.is_done } : it)),
            },
      ),
    );
  };

  const removeItem = (listIndex: number, itemIndex: number) => {
    const previous = checklists[listIndex]?.items[itemIndex - 1];
    if (previous) focusNext.current = previous.id;
    onChange(
      checklists.map((cl, i) =>
        i !== listIndex ? cl : { ...cl, items: cl.items.filter((_, j) => j !== itemIndex) },
      ),
    );
    if (previous) itemRefs.current.get(previous.id)?.focus();
  };

  return (
    <div className="space-y-3">
      {checklists.map((checklist, listIndex) => (
        <div key={listIndex} className="rounded-lg border border-border">
          <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
            <Icon d={FORM_ICONS.checklist} className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              type="text"
              value={checklist.title}
              onChange={(e) => updateTitle(listIndex, e.target.value)}
              aria-label={t('task.checklistTitle')}
              placeholder={t('task.checklistTitle')}
              className="flex-1 rounded-sm border-0 bg-transparent text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={() => removeChecklist(listIndex)}
              aria-label={t('task.createForm.removeChecklist', { title: checklist.title })}
              className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Icon d={FORM_ICONS.x} className="h-3.5 w-3.5" />
            </button>
          </div>

          <ul className="space-y-1 px-3 py-2">
            {checklist.items.map((item, itemIndex) => (
              <li key={item.id} className="group flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={item.is_done}
                  onChange={() => toggleItem(listIndex, itemIndex)}
                  aria-label={item.content || t('task.checklistItemPlaceholder')}
                  className="h-4 w-4 shrink-0 rounded border-input text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <input
                  ref={(el) => registerItem(item.id, el)}
                  type="text"
                  value={item.content}
                  onChange={(e) => updateItem(listIndex, itemIndex, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addItem(listIndex, itemIndex);
                    } else if (e.key === 'Backspace' && e.currentTarget.value === '') {
                      e.preventDefault();
                      removeItem(listIndex, itemIndex);
                    }
                  }}
                  aria-label={t('task.checklistItemPlaceholder')}
                  placeholder={t('task.checklistItemPlaceholder')}
                  className={[
                    'flex-1 rounded-sm border-0 bg-transparent text-sm outline-none',
                    'placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring',
                    item.is_done ? 'text-muted-foreground line-through' : 'text-foreground',
                  ].join(' ')}
                />
                <button
                  type="button"
                  onClick={() => removeItem(listIndex, itemIndex)}
                  aria-label={t('task.createForm.removeChecklistItem')}
                  className="rounded-sm p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                >
                  <Icon d={FORM_ICONS.x} className="h-3 w-3" />
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => addItem(listIndex)}
                className="flex items-center gap-1.5 rounded-sm py-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon d={FORM_ICONS.plus} className="h-3 w-3" />
                {t('task.addChecklistItem')}
              </button>
            </li>
          </ul>
        </div>
      ))}

      <button
        type="button"
        onClick={addChecklist}
        className="flex items-center gap-1.5 rounded-sm text-xs font-medium text-primary transition-colors hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Icon d={FORM_ICONS.checklist} className="h-3.5 w-3.5" />
        {t('task.addChecklist')}
      </button>
    </div>
  );
}
